<template>
  <transition name="fade">
    <div v-if="isOpen" class="dark-fullscreen-modal" @click.self="doCancel">
      <div class="modal-header">
        <div class="file-info">
          <span class="file-name">{{ file.originalName }}</span>
        </div>
        <button class="btn-close-custom" @click="doCancel" :title="$t('close')">
          &times;
        </button>
      </div>

      <div class="modal-body scrollbar" :class="{ 'docx-mode': fileExtension === 'docx' }" v-loader.opacity="isLoading" @click.self="doCancel">
        <template v-if="previewFileUrl">
          <!-- PDF -->
          <div v-if="fileExtension === 'pdf' && pdfData" class="doc-container" @click.self="doCancel">
            <pdf-vuer 
              v-for="index in numPages" 
              :key="index" 
              :id="`page-${index}`" 
              :page="index" 
              :src="pdfData" 
              scale="page-width"
              class="document-page"
            />
          </div>

          <!-- DOCX -->
          <div 
            v-else-if="fileExtension === 'docx'" 
            ref="docxContainer" 
            class="docx-vue-container"
            @click.self="doCancel">
          </div>

          <!-- TXT -->
          <div v-else-if="fileExtension === 'txt' && textData" class="doc-container" @click.self="doCancel">
            <div class="document-page text-page" @click.self="doCancel">
              <pre>{{ textData }}</pre>
            </div>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active {
  @include                                transition(opacity 0.3s);
}

.fade-enter-from, .fade-leave-to {
  opacity:                                0;
}

.dark-fullscreen-modal {
  position:                               fixed;
  top:                                    0;
  left:                                   0;
  width:                                  100vw;
  height:                                 100vh;
  background-color:                       rgba(0, 0, 0, 0.95);
  z-index:                                9999;
  display:                                flex;
  flex-direction:                         column;
}

.modal-header {
  height:                                 60px;
  display:                                flex;
  justify-content:                        space-between;
  align-items:                            center;
  padding:                                0 24px;
  background-color:                       rgba(0, 0, 0, 0.5);
  border-bottom:                          1px solid rgba(255, 255, 255, 0.1);

  .file-name {
    color:                                $white;
    font-size:                            var(--font-size-16);
    font-weight:                          500;
  }

  .btn-close-custom {
    background:                           transparent;
    border:                               none;
    color:                                $spanish-gray;
    font-size:                            32px;
    line-height:                          1;
    cursor:                               pointer;
    padding:                              0 10px;
    @include                              transition(color 0.2s);

    &:hover {
      color:                              $white;
    }
  }
}

.modal-body {
  flex:                                   1;
  overflow-y:                             auto;
  overflow-x:                             hidden;
  padding:                                0 24px 24px 24px;
  display:                                flex;
  justify-content:                        center;
  align-items:                            flex-start;
}

.doc-container {
  width:                                  100%;
  max-width:                              1000px;
  background:                             transparent;
  display:                                flex;
  flex-direction:                         column;
  gap:                                    16px;
}

.modal-body.docx-mode {
  padding:                                0 !important;
  display:                                block !important; 
}

.docx-vue-container {
  width:                                  100%;
  min-height:                             100%;
  padding:                                0 24px 24px 24px;
  background:                             transparent;
  display:                                flex;
  flex-direction:                         column;
  align-items:                            center;
  gap:                                    24px;

  :deep(section.docx) {
    background:                           $white !important;
    width:                                100% !important; 
    max-width:                            1000px !important; 
    box-sizing:                           border-box !important; 
    box-shadow:                           0 4px 12px rgba(0,0,0,0.5) !important; 
    margin-bottom:                        0 !important;
    flex-shrink:                          0 !important;
  }
}

.document-page {
  background:                             $white;
}

.document-page:deep(canvas) {
  display:                                block;
  width:                                  100% !important;
  height:                                 auto !important;
}

.text-page {
  padding:                                40px;
  min-height:                             90vh;
  
  pre {
    white-space:                          pre-wrap;
    font-family:                          monospace;
    color:                                $raisin-black;
    margin:                               0;
    font-size:                            var(--font-size-14);
  }
}

@media (max-width: 767px) {
  .modal-body {
    padding:                              8px !important;
  }
  
  .modal-header {
    padding:                              0 16px;
  }

  .docx-vue-container {
    padding:                              8px;
    
    :deep(section.docx) {
      width:                              800px !important; 
      max-width:                          800px !important;
      zoom:                               0.5 !important; 
      margin:                             0 auto !important;
    }
  }
}
</style>

<script>
import { renderAsync } from 'docx-preview'
import { createLoadingTask } from 'pdfvuer';

const SUPPORTED_OFFCANVAS_DOCS = ['pdf', 'docx', 'txt'];

export default {
  name: "BaseFilePreviewModal",
  data: () => ({
    isOpen: false,
    resolvePromise: null,
    rejectPromise: null,
    isLoading: false,
    alert: {
      message: null,
      class: null
    },
    previewFileUrl: null,
    pdfData: null,
    numPages: 0,
    file: {},
    textData: null,
  }),
  computed: {
    fileExtension() {
      return this.file?.extension?.toLowerCase() || 'other';
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    async show(options = {}) {
      this.resetData();
      this.file = options.file || {};

      if (!SUPPORTED_OFFCANVAS_DOCS.includes(this.fileExtension)) {
        return Promise.resolve('unsupported'); 
      }

      this.isOpen = true;
      this.isLoading = true;

      if (this.fileExtension === 'pdf' && options.url) {
        await this.loadPdfData(options.url);
      } else if (this.fileExtension === 'docx' && options.url) {
        await this.loadDocxData(options.url);
      } else if (this.fileExtension === 'txt' && options.url) {
        await this.loadTextData(options.url);
      }

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    async loadPdfData(originalUrl) {
      try {
        const response = await fetch(originalUrl, { credentials: 'include' });

        if (response.ok) {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);

          this.previewFileUrl = objectUrl;                        
          const loadingTask = createLoadingTask(objectUrl);
          this.pdfData = loadingTask; 

          const pdf = await loadingTask; 
          this.numPages = pdf.numPages;
        }
      } catch (error) {
        this.doCancel();

        if (error.name === 'InvalidPDFException' || (error.message && error.message.toLowerCase().includes('pdf'))) {
          this.$message.popup(this, 'exam.invalidCorruptedFile', 'operationFail', 'error');
        } else {
          this.$message.popup(this, 'request_error', 'operationFail', 'error');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async loadDocxData(originalUrl) {
      try {
        const response = await fetch(originalUrl, { credentials: 'include' });

        if (response.ok) {
          const blob = await response.blob();
          this.previewFileUrl = 'docx-loaded';
          
          await this.$nextTick();
          const container = this.$refs.docxContainer;

          if (container) {
            await renderAsync(blob, container, container, {
              className: "docx",
              inWrapper: false,
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              breakPages: true
            });
          }
        }
      } catch (error) {
        this.doCancel();
        
        if (error.message && error.message.includes("Can't find end of central directory")) {
          this.$message.popup(this, 'exam.invalidCorruptedFile', 'operationFail', 'error');
        } else {
          this.$message.popup(this, 'request_error', 'operationFail', 'error');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async loadTextData(originalUrl) {
      try {
        const response = await fetch(originalUrl, { credentials: 'include' });

        if (response.ok) {
          this.textData = await response.text();
          this.previewFileUrl = 'text-loaded'; 
        }
      } catch (error) {
        this.doCancel();
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    resetData() {
      if (this.previewFileUrl && this.previewFileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewFileUrl);
      }

      this.previewFileUrl = null;
      this.pdfData = null;
      this.numPages = 0;
      this.file = {};
      this.textData = null;
    },
    doCancel() {   
      this.resetData();
      this.isOpen = false;
      if (this.resolvePromise) this.resolvePromise(false);
    },
    handleKeyDown(event) {
      if (this.isOpen && event.key === 'Escape') {
        this.doCancel();
      }
    },
  }
}
</script>